/**
 * Owner: Masa Bayu
 * File: demo-library-store.ts
 * Date:Time: 2026-09-22 05:20 (pecah §377 2026-10-18)
 * Description: T4 library store — R-02 seeds from active catalog port (prod SoT
 *   when bound). §377: state + seed + projeksi di demo-library-base; query/
 *   resume/version/hub-grants di demo-library-extended; fail ini mengekalkan
 *   semua eksport awam melalui re-export. API luaran tidak berubah.
 */

import {
  applyReadingProgress,
  authorizeLibraryReaderLaunch,
  blockLibraryItemForRevokedEntitlement,
  createQxreadEntitlement,
  createWishlistItem,
  evaluateLibraryOfflineAvailability,
  markLibraryItemOfflineReady,
  recordLibraryContinueReading,
  type QxreadLibraryItem,
  type QxreadLibraryReaderLaunchIntent,
  type QxreadWishlistItem,
} from '@qx/qxread-core';
import { getCatalogEntry, listCatalog } from '../publication/catalog-port';
import {
  DEMO_ACCOUNT_ID,
  demoLibraryState,
  getDemoLibraryProjection,
  record,
} from './demo-library-base';

export * from './demo-library-base';
export * from './demo-library-extended';

export function startDemoReading(publicationId: string): QxreadLibraryItem {
  getDemoLibraryProjection();
  const entitlement = demoLibraryState.entitlements.find((item) => item.publicationId === publicationId);
  if (entitlement === undefined) {
    throw new Error('Demo library entitlement not found');
  }
  const current = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (current === undefined) {
    getDemoLibraryProjection();
  }
  const item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (item === undefined) {
    throw new Error('Demo library item missing after projection');
  }
  const updated = recordLibraryContinueReading(item, {
    percentComplete: Math.max(item.progress.percentComplete, 12),
    lastPositionRef: `pos_${item.publicationId}_demo`,
  });
  demoLibraryState.itemsByEntitlementId.set(entitlement.entitlementId, updated);
  record('qxread.library.progress_updated', {
    publicationId,
    progressState: updated.progress.state,
    percent: updated.progress.percentComplete,
    lastPositionRef: updated.progress.lastPositionRef,
  });
  return updated;
}

export function completeDemoReading(publicationId: string): QxreadLibraryItem {
  getDemoLibraryProjection();
  const entitlement = demoLibraryState.entitlements.find((item) => item.publicationId === publicationId);
  if (entitlement === undefined) {
    throw new Error('Demo library entitlement not found');
  }
  const item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (item === undefined) {
    throw new Error('Demo library item not found');
  }
  const updated = applyReadingProgress(item, {
    state: 'completed',
    percentComplete: 100,
  });
  demoLibraryState.itemsByEntitlementId.set(entitlement.entitlementId, updated);
  record('qxread.library.progress_updated', {
    publicationId,
    progressState: 'completed',
    percent: 100,
  });
  return updated;
}

export function addDemoWishlistTitle(publicationId: string): QxreadWishlistItem {
  const entry = getCatalogEntry(publicationId) ?? listCatalog().find((item) => item.publicationId === publicationId);
  if (entry === undefined || entry === null) {
    throw new Error('Wishlist publication not found in active catalog');
  }
  const existing = demoLibraryState.wishlist.find(
    (item) => item.publicationId === publicationId && item.state === 'active',
  );
  if (existing !== undefined) {
    return existing;
  }
  const created = createWishlistItem({
    accountId: DEMO_ACCOUNT_ID,
    publicationId: entry.publicationId,
    title: entry.title,
  });
  demoLibraryState.wishlist = [...demoLibraryState.wishlist, created];
  return created;
}

export function removeDemoWishlistTitle(publicationId: string): void {
  demoLibraryState.wishlist = demoLibraryState.wishlist.filter((item) => item.publicationId !== publicationId);
}

export function requestDemoOffline(publicationId: string): {
  readonly allowed: boolean;
  readonly reason: string;
} {
  getDemoLibraryProjection();
  const entitlement = demoLibraryState.entitlements.find((item) => item.publicationId === publicationId);
  if (entitlement === undefined) {
    throw new Error('Demo offline entitlement not found');
  }
  let item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (item === undefined) {
    getDemoLibraryProjection();
    item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  }
  if (item === undefined) {
    throw new Error('Demo offline library item not found');
  }
  const decision = evaluateLibraryOfflineAvailability({ item, entitlement });
  record('qxread.library.offline_decision', {
    publicationId,
    allowed: decision.allowed,
    reason: decision.reason,
  });
  if (decision.allowed && decision.nextItemState === 'offline_preparing') {
    const preparing = Object.freeze({
      ...item,
      state: 'offline_preparing' as const,
      updatedAt: new Date().toISOString(),
    });
    demoLibraryState.itemsByEntitlementId.set(entitlement.entitlementId, markLibraryItemOfflineReady(preparing));
  }
  return Object.freeze({ allowed: decision.allowed, reason: decision.reason });
}

export function requestDemoReaderLaunch(publicationId: string): QxreadLibraryReaderLaunchIntent {
  getDemoLibraryProjection();
  const entitlement = demoLibraryState.entitlements.find((item) => item.publicationId === publicationId);
  if (entitlement === undefined) {
    throw new Error('Demo launch entitlement not found');
  }
  const item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (item === undefined) {
    throw new Error('Demo launch library item not found');
  }
  demoLibraryState.lastLaunchIntent = authorizeLibraryReaderLaunch({
    item,
    entitlement,
    sessionAuthenticated: true,
    accountId: DEMO_ACCOUNT_ID,
  });
  record('qxread.library.launch_intent', {
    publicationId,
    allowed: demoLibraryState.lastLaunchIntent.allowed,
    executesReader: false,
  });
  return demoLibraryState.lastLaunchIntent;
}

export function revokeDemoEntitlement(publicationId: string): QxreadLibraryItem {
  getDemoLibraryProjection();
  const index = demoLibraryState.entitlements.findIndex((item) => item.publicationId === publicationId);
  if (index < 0) {
    throw new Error('Demo revoke entitlement not found');
  }
  const current = demoLibraryState.entitlements[index];
  if (current === undefined) {
    throw new Error('Demo revoke entitlement missing');
  }
  const revoked = createQxreadEntitlement({
    entitlementId: current.entitlementId,
    accountId: current.accountId,
    publicationId: current.publicationId,
    scope: current.scope,
    state: 'revoked',
    createdAt: current.createdAt,
  });
  demoLibraryState.entitlements = demoLibraryState.entitlements.map((item, i) => (i === index ? revoked : item));
  const item = demoLibraryState.itemsByEntitlementId.get(revoked.entitlementId);
  if (item === undefined) {
    throw new Error('Demo revoke library item not found');
  }
  const blocked = blockLibraryItemForRevokedEntitlement(item, revoked);
  demoLibraryState.itemsByEntitlementId.set(revoked.entitlementId, blocked);
  void import('../pwa/package-purge')
    .then(({ purgePackageOnRevoke }) =>
      purgePackageOnRevoke({
        accountId: revoked.accountId,
        contentId: publicationId,
        entitlement: revoked,
      }),
    )
    .catch(() => {
      /* purge best-effort — access SoT already revoked */
    });
  return blocked;
}
